import React, { useState } from "react";
import { X } from "lucide-react";

// ===== RESPONSIVE ADD EMPLOYEE MODAL COMPONENT =====
const AddEmployeeModal = ({
  styles,
  text,
  language,
  theme,
  screenSize,
  onAdd,
  onClose,
}) => {
  // Initialize form data with empty values
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    department: "",
    position: "",
    laborMode: "",
    company: "",
    clientCode: "",
    status: "Active",
    salary: "",
    nationalId: "",
    address: "",
    city: "",
    country: "Egypt",
    nationality: "",
    gender: "",
    maritalStatus: "",
    dateOfBirth: "",
  });

  // Form validation errors state
  const [formErrors, setFormErrors] = useState({});

  // Handle input changes
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    // Clear error when user starts typing
    if (formErrors[e.target.name]) {
      setFormErrors({
        ...formErrors,
        [e.target.name]: "",
      });
    }
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};

    // Check required fields
    if (!formData.name.trim()) {
      errors.name = text[language].nameRequired;
    }

    if (!formData.email.trim()) {
      errors.email = text[language].emailRequired;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = text[language].emailInvalid;
    }

    if (!formData.department.trim()) {
      errors.department = text[language].departmentRequired;
    }

    if (!formData.position.trim()) {
      errors.position = text[language].positionRequired;
    }

    // Update errors state
    setFormErrors(errors);
    // Return true if no errors
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only proceed if form is valid
    if (!validateForm()) {
      return;
    }

    // Call add employee function
    await onAdd(formData);
  };

  // Return modal JSX
  return (
    <div
      style={styles.modal}
      onClick={onClose}
    >
      <div
        style={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{text[language].addNewEmployee}</h2>
          <button
            style={styles.closeBtn}
            onClick={onClose}
          >
            <X size={screenSize === "xs" ? 20 : 24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Basic Information Section */}
          <div style={styles.sectionTitle}>
            {text[language].basicInformation}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].fullName} *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                style={{
                  ...styles.formInput,
                  borderColor: formErrors.name
                    ? "#ef4444"
                    : theme === "dark"
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
                placeholder={text[language].enterFullName}
              />
              {formErrors.name && (
                <span style={styles.errorText}>{formErrors.name}</span>
              )}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].emailAddress} *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                style={{
                  ...styles.formInput,
                  borderColor: formErrors.email
                    ? "#ef4444"
                    : theme === "dark"
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
                placeholder={text[language].enterEmail}
              />
              {formErrors.email && (
                <span style={styles.errorText}>{formErrors.email}</span>
              )}
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].phoneNumber}
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterPhone}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].nationalId}
              </label>
              <input
                type="text"
                name="nationalId"
                value={formData.nationalId}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterNationalId}
              />
            </div>
          </div>

          {/* Work Information Section */}
          <div style={styles.sectionTitle}>
            {text[language].workInformation}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].department} *
              </label>
              <select
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                style={{
                  ...styles.formSelect,
                  borderColor: formErrors.department
                    ? "#ef4444"
                    : theme === "dark"
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
              >
                <option value="">{text[language].selectDepartment}</option>
                <option value="Information Technology">
                  {text[language].infotech}
                </option>

                <option value="Human Resources">
                  {text[language].humanResources}
                </option>
                <option value="Finance">{text[language].finance}</option>
                <option value="Marketing">{text[language].marketing}</option>
                <option value="Operations">
                  {text[language].operations}
                </option>
                <option value="Sales">{text[language].sales}</option>
                <option value="Customer Service">
                  {text[language].customerService}
                </option>
              </select>
              {formErrors.department && (
                <span style={styles.errorText}>{formErrors.department}</span>
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].position} *
              </label>
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                style={{
                  ...styles.formInput,
                  borderColor: formErrors.position
                    ? "#ef4444"
                    : theme === "dark"
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
                placeholder={text[language].enterPosition}
              />
              {formErrors.position && (
                <span style={styles.errorText}>{formErrors.position}</span>
              )}
            </div>
          </div>

          {/* Show client code field only if company is not "infinity" */}
          {formData.company &&
            formData.company.toLowerCase() !== "infinity" && (
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].clientCode}
                </label>
                <input
                  type="number"
                  name="clientCode"
                  value={formData.clientCode}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  placeholder={text[language].enterClientCode}
                />
              </div>
            )}

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].statusLabel}
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                style={styles.formSelect}
              >
                <option value="Active">{text[language].statusActive}</option>
                <option value="OnLeave">
                  {text[language].statusOnLeave}
                </option>
                <option value="Inactive">
                  {text[language].statusInactive}
                </option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].salaryLabel}
              </label>
              <input
                type="number"
                name="salary"
                value={formData.salary}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterSalary}
                min="0"
              />
            </div>
          </div>

          {/* Personal Information Section */}
          <div style={styles.sectionTitle}>
            {text[language].personalInformation}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].dateOfBirth}
              </label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                style={styles.formInput}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{text[language].gender}</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                style={styles.formSelect}
              >
                <option value="Select Gender">
                  {text[language].selectGender}
                </option>
                <option value="Male">{text[language].male}</option>
                <option value="Female">{text[language].female}</option>
              </select>
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].maritalStatus}
              </label>
              <select
                name="maritalStatus"
                value={formData.maritalStatus}
                onChange={handleInputChange}
                style={styles.formSelect}
              >
                <option value="">{text[language].selectMaritalStatus}</option>
                <option value="Single">{text[language].single}</option>
                <option value="Married">{text[language].married}</option>
                <option value="Divorced">{text[language].divorced}</option>
                <option value="Widowed">{text[language].widowed}</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].nationality}
              </label>
              <input
                type="text"
                name="nationality"
                value={formData.nationality}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterNationality}
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{text[language].city}</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterCity}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{text[language].country}</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterCountry}
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{text[language].address}</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              style={styles.formInput}
              placeholder={text[language].enterAddress}
            />
          </div>

          {/* Modal Actions */}
          <div style={styles.modalActions}>
            <button
              type="button"
              style={styles.secondaryBtn}
              onClick={onClose}
            >
              {text[language].cancel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEmployeeModal;
